import React from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { WizardProvider } from '../contexts/WizardContext';
import OnboardingIntro from './Onboarding/OnboardingIntro';
import StepChild       from './Onboarding/StepChild';
import StepLocation    from './Onboarding/StepLocation';
import StepChallenges  from './Onboarding/StepChallenges';
import StepCurrentNeeds from './Onboarding/StepCurrentNeeds';
import StepBenefits    from './Onboarding/StepBenefits';
import { authFetch }  from '../utils/auth';
import { useProfile } from '../contexts/ProfileContext';


export default function Onboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loaded: profileLoaded, refresh } = useProfile();
  
  // Wait for profile to be loaded before determining the next step
  // Show a loading indicator while waiting
  if (!profileLoaded) {
    return <div className="flex justify-center items-center h-screen">Laster...</div>;
  }
  
  // Determine the first incomplete step
  // If someone hits /onboarding directly, always show intro page
  if (location.pathname === '/onboarding') {
    return <Navigate to="/onboarding/intro" replace />;
  }

  const finish = async (draft) => {
    try {
      // Show loading state immediately
      const loadingElement = document.createElement('div');
      loadingElement.className = 'fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50';
      loadingElement.innerHTML = `
        <div class="text-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p class="text-text-secondary">Setter opp din personlige assistent...</p>
        </div>
      `;
      document.body.appendChild(loadingElement);
      
      // Make sure profileComplete is set to true and clear any skip tracking
      const finalData = { 
        ...draft, 
        profileComplete: true,
        onboardingSkipped: false,
        lastOnboardingStep: null
      };
      
      await authFetch('/profile', {
        method: 'PUT',
        headers: { 'Content-Type':'application/json' },
        body: JSON.stringify(finalData)
      });
      
      // Wait until shared context is up-to-date
      await refresh();
      
      // Create welcome conversation with wizard context
      try {
        const welcomeResponse = await authFetch('/api/conversations', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: 'Velkommen til Coord!',
            initialMessage: 'Start',
            isWelcomeConversation: true,
            wizardContext: {
              childName: draft?.child?.alias || '',
              childAge: draft?.child?.birthYear ? new Date().getFullYear() - draft.child.birthYear : null,
              diagnoses: draft?.child?.diagnoses || [],
              kommune: draft?.kommune || '',
              schoolStage: draft?.schoolStage || '',
              challenges: draft?.challenges || [],
              currentBenefits: draft?.currentBenefits || []
            }
          }),
        });
        
        if (welcomeResponse.ok) {
          const conversation = await welcomeResponse.json();
          // Small delay to ensure loading overlay is visible
          await new Promise(resolve => setTimeout(resolve, 500));
          // Navigate smoothly to the conversation
          window.location.href = `/conversations/${conversation._id}`;
          return;
        }
      } catch (convError) {
        console.error('Error creating welcome conversation:', convError);
        // Fall back to dashboard if conversation creation fails
      }
      
      // Remove loading overlay before fallback navigation
      document.body.removeChild(loadingElement);
      
      // Fallback: Guard now sees correct flags because it uses the same context
      navigate('/home', { replace: true });
    } catch (error) {
      console.error('Error updating profile:', error);
      // Remove loading overlay on error
      const loadingElement = document.querySelector('.fixed.inset-0.bg-white');
      if (loadingElement) document.body.removeChild(loadingElement);
      alert('Det oppstod en feil ved lagring av profilen. Vennligst prøv igjen.');
    }
  };

  return (
    <WizardProvider>
      <Routes>
        <Route path="intro" element={<OnboardingIntro navigate={navigate} />} />
        <Route path="child" element={<StepChild navigate={navigate} />} />
        <Route path="location" element={<StepLocation navigate={navigate} />} />
        <Route path="challenges" element={<StepChallenges navigate={navigate} />} />
        <Route path="current-needs" element={<StepCurrentNeeds navigate={navigate} />} />
        <Route path="benefits" element={<StepBenefits navigate={navigate} finish={finish} />} />
        <Route path="*" element={<OnboardingIntro navigate={navigate} />} />
      </Routes>
    </WizardProvider>
  );
}
